import imgRectangle from '../../../assets/PGRM-logo-color.png';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth/useAuth';
import { useEffect, useRef, useState } from 'react';
import styles from './AppHeader.module.scss';

interface AppHeaderProps {
  readonly onLogin: () => void;
  readonly onLogout: () => void;
}

export function AppHeader({ onLogin, onLogout }: AppHeaderProps) {
  const auth = useAuth();
  const loggedIn = auth?.isAuthenticated ?? false;
  const nome = auth?.user?.name ?? '';
  const imagem = auth?.user?.picture ?? '';
  const firstLetter = nome.charAt(0).toUpperCase();

  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    setOpen(false);
    onLogout();
  };

  const location = useLocation();
  const handleManuaisClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      document
        .getElementById('manuais')
        ?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <header className={styles.header} data-name='app-header'>
      <div className={styles.row}>
        <div className={styles.brandWrap}>
          <button
            type='button'
            className={styles.hamburger}
            onClick={() => setMenuOpen((m) => !m)}
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
          >
            <i
              className={`fas fa-${menuOpen ? 'times' : 'bars'}`}
              aria-hidden='true'
            />
          </button>
          <img alt='Logo PGRM' className={styles.logo} src={imgRectangle} />
          <div className={styles.brandText}>
            <p className={styles.brandTitle}>
              Plataforma de Gestão de Recursos Minerais
            </p>
            <p className={styles.brandSub}>Ministério de Minas e Energia</p>
          </div>
        </div>
        <nav className={styles.navLinks} aria-label='Navegação principal'>
          <Link to='/' className={styles.navLink}>
            Home
          </Link>
          <Link to='/resolucoes' className={styles.navLink}>
            Resoluções
          </Link>
          <Link to='/eventos' className={styles.navLink}>
            Eventos
          </Link>
          <Link
            to='/#manuais'
            className={styles.navLink}
            onClick={handleManuaisClick}
          >
            Manuais PGRM
          </Link>
        </nav>
        <div className={styles.rightAction}>
          {loggedIn ? (
            <div className={styles.avatarWrap} ref={wrapRef}>
              <button
                type='button'
                className={styles.trigger}
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                aria-label='Menu do usuário'
              >
                {imagem ? (
                  <img src={imagem} alt={nome} className={styles.avatarImg} />
                ) : (
                  <span className={styles.avatarCircle}>
                    {firstLetter || (
                      <i className={`fas fa-user`} aria-hidden='true' />
                    )}
                  </span>
                )}
                <span className={styles.userName}>{nome}</span>
                <i
                  className={`fas fa-chevron-${open ? 'up' : 'down'} ${styles.caret}`}
                  aria-hidden='true'
                />
              </button>

              {open && (
                <div className={styles.dropdown} role='menu'>
                  <div className={styles.dropdownHeader}>
                    <span className={styles.dropdownAvatarCircle}>
                      {imagem ? (
                        <img
                          src={imagem}
                          alt={nome}
                          className={styles.avatarImg}
                        />
                      ) : (
                        firstLetter || (
                          <i className={'fas fa-user'} aria-hidden='true' />
                        )
                      )}
                    </span>
                    <div className={styles.dropdownInfo}>
                      <p className={styles.dropdownLabel}>Logado como</p>
                      <p className={styles.dropdownName}>{nome}</p>
                    </div>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <button
                    type='button'
                    className={styles.logoutBtn}
                    onClick={handleLogout}
                  >
                    <i className={`fas fa-sign-out-alt`} aria-hidden='true' />
                    <span>Sair</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button type='button' className={styles.ctaGreen} onClick={onLogin}>
              <i className={`fas fa-city`} aria-hidden='true' />
              <span>Acessar a PGRM</span>
            </button>
          )}
        </div>
      </div>

      {menuOpen && (
        <nav className={styles.mobileMenu} aria-label='Menu de navegação'>
          <Link
            to='/'
            className={styles.mobileNavLink}
            onClick={() => setMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to='/resolucoes'
            className={styles.mobileNavLink}
            onClick={() => setMenuOpen(false)}
          >
            Resoluções
          </Link>
          <Link
            to='/eventos'
            className={styles.mobileNavLink}
            onClick={() => setMenuOpen(false)}
          >
            Eventos
          </Link>
          <Link
            to='/#manuais'
            className={styles.mobileNavLink}
            onClick={(e) => {
              setMenuOpen(false);
              handleManuaisClick(e);
            }}
          >
            Manuais PGRM
          </Link>
        </nav>
      )}
    </header>
  );
}
